<template>
  <div>
    <label v-if="label" :for="fieldId" class="label-caps field-label">{{ label }}</label>
    <slot :id="fieldId" :invalid="!!error" :described-by="describedBy" />
    <p v-if="error" :id="`${fieldId}-error`" class="form-error" role="alert">{{ error }}</p>
    <p v-else-if="hint" :id="`${fieldId}-hint`" class="form-hint">{{ hint }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, useId } from "vue"

/**
 * Label + control + error/hint wrapper — the field triad every form used to
 * retype. The control goes in the slot and wires itself up via slot props:
 *
 *   <FormField :label="$t('common.amount')" :error="amountError" v-slot="{ id, invalid, describedBy }">
 *     <input :id="id" class="input" :aria-invalid="invalid || undefined" :aria-describedby="describedBy" ... />
 *   </FormField>
 *
 * For a plain text input, use AppInput which composes this for you.
 */
const props = defineProps<{
  label?: string
  error?: string
  hint?: string
  /** Explicit control id; auto-generated when omitted. */
  id?: string
}>()

const generatedId = useId()
const fieldId = computed(() => props.id ?? generatedId)
const describedBy = computed(() =>
  props.error ? `${fieldId.value}-error` : props.hint ? `${fieldId.value}-hint` : undefined,
)
</script>
